export const DEFAULTS = {
  "validacoes.email_paciente_obrigatorio": true,
  "validacoes.email_cuidador_obrigatorio": true,
  "receptivo.origens": ["medico", "laboratorio", "paciente", "responsavel", "outro"],
  "agendamento.medico_solicitante": false,
  "agendamento.observacoes": false,
  "agendamento.local_procedimento": false,
  "agendamento.despesas": false,
  "agendamento.aceite_recusa": false,
  // quando false, a despesa deixa de ser somada ao valor do serviço e o faturamento cobra
  // só o serviço — o detalhamento das despesas continua sendo exibido normalmente
  "faturamento.somar_despesas_no_servico": true,
  // tipos que o prestador pode anexar ao agendamento; a operação continua vendo todos
  "agendamento.tipos_arquivo_prestador": ["laudo", "exame"],
  "agendamento.status_lista": [
    "solicitado",
    "liberado",
    "em_analise",
    "finalizado",
    "cancelado",
  ],
};

/**
 * Garante que as configurações do programa estejam em formato de objeto.
 * Aceita:
 *  - null/undefined  → retorna {}
 *  - objeto          → retorna o próprio objeto
 *  - string JSON     → faz JSON.parse e retorna o objeto (ou {} em caso de erro)
 *
 * @param {*} configuracoes - Valor vindo da API/banco (string, objeto, etc.)
 * @returns {object} Objeto de configurações normalizado
 */
export function parseProgramaConfiguracoes(configuracoes) {
  if (!configuracoes) return {};

  // Já é objeto
  if (typeof configuracoes === "object") {
    return configuracoes ?? {};
  }

  // Veio como string
  if (typeof configuracoes === "string") {
    const str = configuracoes.trim();
    if (!str) return {};

    try {
      const parsed = JSON.parse(str);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      console.error("Falha ao interpretar configurações do programa:", error);
      return {};
    }
  }

  // Qualquer outro tipo inesperado
  return {};
}

/**
 * Busca um valor aninhado em um objeto de configuração.
 *
 * - Se o caminho não existir nas configurações do programa,
 *   tenta pegar o valor em DEFAULTS[path].
 * - Se também não existir em DEFAULTS, usa o fallback.
 *
 * @param {*} configuracoes - Objeto ou string JSON com as configurações.
 * @param {string} path - Caminho do valor (ex: "agendamento.medico_solicitante").
 * @param {*} fallback - Valor padrão final, caso não exista em cfg nem em DEFAULTS.
 * @returns {*} O valor encontrado, ou DEFAULTS[path], ou fallback.
 */
export function getConfig(configuracoes, path, fallback) {
  const cfg = parseProgramaConfiguracoes(configuracoes);
  const parts = path.split(".");
  let cur = cfg;

  for (const p of parts) {
    if (cur && typeof cur === "object" && p in cur) {
      cur = cur[p];
    } else {
      // Não encontrou no JSON: tenta nos defaults
      return Object.prototype.hasOwnProperty.call(DEFAULTS, path)
        ? DEFAULTS[path]
        : fallback;
    }
  }

  // Se encontrou o caminho mas o valor é undefined, também cai no default/fallback
  if (typeof cur === "undefined") {
    return Object.prototype.hasOwnProperty.call(DEFAULTS, path)
      ? DEFAULTS[path]
      : fallback;
  }

  return cur;
}

/**
 * O layout novo das listagens (/atendimentos e /agendamentos) está em validação e vale
 * apenas para os programas que pedirem, gravando em suas configurações:
 *
 *   "agendamento": { "layout_listas": true }
 *
 * É opt-in: sem a chave, vale a tabela anterior. Assim o novo formato fica restrito aos
 * programas em teste e nenhum outro cliente é surpreendido. Onde não há programa em
 * contexto (admin na listagem global), também vale a tabela anterior.
 *
 * Terminada a validação, a ideia é que o layout novo passe a valer para todos: basta
 * remover esta função, os dois ramos de render e o renderRows antigo de cada tela.
 */
export function usaLayoutNovoListas(configuracoes) {
    return getConfig(configuracoes, 'agendamento.layout_listas', false) === true;
}

/**
 * Perguntas do `Termos_Dinamicos` que se aplicam à categoria de um profissional.
 *
 * Cada pergunta pode declarar `categorias: ["medicos", "multiprofissionais"]`. Quem não
 * declara vale só para médico — que era o único público até aqui, então nenhum programa
 * muda de comportamento.
 *
 * Existe porque médico e nutricionista assinam TCLEs diferentes: duas das três perguntas
 * são idênticas, mas a da Categoria B de risco na gravidez não pode ser. O médico declara
 * que a conduta é dele ("cabendo-me avaliar a relação risco-benefício"); a nutricionista
 * reconhece que a conduta é do médico. Servir a pergunta do médico para ela faria assumir
 * responsabilidade clínica que não é da profissão dela.
 *
 * O backend aplica o mesmo recorte antes de validar — as duas pontas precisam concordar,
 * senão a nutricionista receberia "Campo obrigatório" em pergunta que nunca viu.
 */
export function perguntasDaCategoria(questions, categoriaPrestador) {
    if (!Array.isArray(questions)) return [];

    // sem categoria conhecida, mostra todas: esconder tudo travaria o aceite
    if (!categoriaPrestador) return questions.filter((q) => q && typeof q === 'object');

    return questions.filter((q) => {
        if (!q || typeof q !== 'object') return false;
        const categorias = Array.isArray(q.categorias) && q.categorias.length ? q.categorias : ['medicos'];
        return categorias.includes(categoriaPrestador);
    });
}
